import { Box, BoxProps } from '@material-ui/core';
import React from 'react';

import { Font } from '@/components/shared/components/Font';
import { SizedIconWithCircle } from '@/components/shared/components/SizedIconWithCircle';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export function IconHeader({
  iconName,
  labelTid,
  surLabelTid,
  subLabelTid,
  labelComponent,
  surLabelComponent,
  subLabelComponent,
  color,
  variant = 'medium',
  borderRadius,
  subLabelVariant = 'overline',
  actionsOptionsArray,
  copyToClipboard,
  forceSingleLine = false,
  boxProps,
}: {
  iconName?: string;
  labelTid?: string;
  surLabelTid?: string;
  subLabelTid?: string;
  labelComponent?: React.ReactNode;
  surLabelComponent?: React.ReactNode;
  subLabelComponent?: React.ReactNode;
  subLabelVariant?: 'overline' | 'caption';
  color: 'primary' | 'secondary' | 'minor';
  variant?: 'small' | 'medium' | 'large';
  borderRadius?: string | number;
  copyToClipboard?: { value: string; textId: string };
  forceSingleLine?: boolean;
  actionsOptionsArray?: {
    display: string | React.ReactElement;
    onClick: () => void;
    iconName?: string;
  }[];
  boxProps?: BoxProps;
}) {
  const { palette, customBorderRadius } = useCustomTheme();
  const currentBorderRadius = borderRadius || customBorderRadius.chip;

  const ICON_COLORS = {
    primary: { backgroundColor: palette.primary.main, textColor: palette.primary.contrastText },
    secondary: { backgroundColor: palette.secondary.light, textColor: palette.secondary.contrastText },
    minor: { backgroundColor: palette.minor?.main, textColor: palette.minor?.contrastText },
  };

  return (
    <Box display={'flex'} flexDirection={'row'} alignItems={'center'} {...boxProps}>
      {iconName && (
        <Box pr={1}>
          {iconName.length === 1 ? (
            <Box
              display={'flex'}
              width={40}
              height={40}
              justifyContent={'center'}
              alignItems={'center'}
              borderRadius={currentBorderRadius}
              style={{
                backgroundColor: palette.secondary.main,
                color: palette.secondary.contrastText,
              }}
            >
              <Font variant="subtitle2">{iconName.charAt(0)}</Font>
            </Box>
          ) : (
            <SizedIconWithCircle
              variant={variant}
              backgroundColor={ICON_COLORS[color]?.backgroundColor}
              iconColor={ICON_COLORS[color]?.textColor}
              customBorderRadius={currentBorderRadius}
              iconName={iconName}
              actionsOptionsArray={actionsOptionsArray}
              copyToClipboard={copyToClipboard}
            />
          )}
        </Box>
      )}
      <Box display={'flex'} flexDirection={'column'} textAlign={'start'}>
        {(surLabelTid || surLabelComponent) && (
          <Font variant="overline" color="textSecondary" className="line-clamp-1">
            {surLabelTid ? <Text tId={surLabelTid} /> : surLabelComponent}
          </Font>
        )}

        {(labelTid || labelComponent) && (
          <Font variant="body2" color="title" className={forceSingleLine ? 'overflow-text-ellipsis' : 'line-clamp-2'}>
            <strong>{labelTid ? <Text tId={labelTid} /> : labelComponent}</strong>
          </Font>
        )}

        {(subLabelTid || subLabelComponent) && (
          <Font variant={subLabelVariant} color="textSecondary">
            {subLabelTid ? <Text tId={subLabelTid} /> : subLabelComponent}
          </Font>
        )}
      </Box>
    </Box>
  );
}
